"use client"

import { useState, useEffect } from "react"
import {
  FileText,
  DollarSign,
  UserCheck,
  Target,
  X,
  AlertTriangle,
  UserPlus,
  Building2,
  CheckCircle,
  FolderOpen,
  AlertCircle,
} from "lucide-react"
import { Layout } from "@/shared/components"
import {
  SalesReportManager,
  PayReportManager,
  CommissionRateManager,
  OverrideRateManager,
  AgentManagement as AgentStatusManager,
  AgentAssignmentManager,
  OrganizationManager,
  HRService,
} from "@/features/hr"
import { AgentCampaignSelector } from "@/features/agents"
import { useAuth } from "@/features/auth"
import { useCampaign } from "@/features/campaigns"

interface HRManagerDashboardProps {
  onError: (error: string) => void
}

export default function HRManagerDashboard({ onError }: HRManagerDashboardProps) {
  const { getHRManagerOrganizationId } = useAuth()
  const { selectedCampaign, setSelectedCampaign, isCampaignSelected } = useCampaign()
  
  // Get initial tab from URL hash or default to campaign-selector
  const getInitialTab = () => {
    const hash = window.location.hash.replace('#', '')
    const validTabs = ["campaign-selector", "sales-reports", "pay-reports", "agent-assignment", "commission-rates", "override-rates", "agent-status", "organizations"]
    return validTabs.includes(hash) ? hash : "campaign-selector"
  }

  const [activeTab, setActiveTab] = useState(getInitialTab)
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId)
    // Update URL hash without triggering page reload
    window.history.replaceState(null, '', `#${tabId}`)
  }

  const navigationItems = [
    { id: "campaign-selector", label: "Campaign Selector", icon: FolderOpen },
    { id: "sales-reports", label: "Sales Reports", icon: FileText },
    { id: "pay-reports", label: "Pay Reports", icon: DollarSign },
    { id: "agent-assignment", label: "Agent Assignment", icon: UserPlus },
    { id: "commission-rates", label: "Commission Rates", icon: Target },
    { id: "override-rates", label: "Override Rates", icon: Target },
    { id: "agent-status", label: "Agent Status", icon: UserCheck },
    { id: "organizations", label: "Organizations", icon: Building2 },
  ]

  const handleError = (errorMessage: string) => {
    setError(errorMessage)
    onError(errorMessage)
    setTimeout(() => setError(null), 5000)
  }

  const handleSuccess = (successMessage: string) => {
    setSuccess(successMessage)
    setTimeout(() => setSuccess(null), 5000)
  }

  // Campaign selection requirement component
  const CampaignSelectionRequired = () => (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="text-center max-w-md">
        <div className="bg-yellow-50 border border-yellow-200 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
          <AlertCircle className="w-8 h-8 text-yellow-600" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Campaign Selection Required</h3>
        <p className="text-gray-600 mb-4">
          Please select a campaign from the Campaign Selector tab to view and manage campaign-specific HR data.
        </p>
        <button
          onClick={() => handleTabChange("campaign-selector")}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          Go to Campaign Selector
        </button>
      </div>
    </div>
  )

  const renderContent = () => {
    // Campaign selector is always accessible
    if (activeTab === "campaign-selector") {
      return (
        <AgentCampaignSelector
          selectedCampaignId={selectedCampaign?.id}
          onCampaignSelect={(campaign) => setSelectedCampaign(campaign)}
          showActions={false}
          organizationId={getHRManagerOrganizationId()}
        />
      )
    }

    // All other tabs require campaign selection
    if (!isCampaignSelected) {
      return <CampaignSelectionRequired />
    }

    const commonProps = {
      onError: handleError,
      selectedCampaign: selectedCampaign?.id,
      organizationId: getHRManagerOrganizationId()
    }

    switch (activeTab) {
      case "sales-reports":
        return <SalesReportManager {...commonProps} />
      case "pay-reports":
        return <PayReportManager {...commonProps} />
      case "agent-assignment":
        return <AgentAssignmentManager {...commonProps} />
      case "commission-rates":
        return <CommissionRateManager {...commonProps} />
      case "override-rates":
        return <OverrideRateManager {...commonProps} />
      case "agent-status":
        return <AgentStatusManager {...commonProps} />
      case "organizations":
        return <OrganizationManager onError={handleError} onSuccess={handleSuccess} organizationId={getHRManagerOrganizationId()} />
      default:
        return (
          <div className="card">
            <h2 className="text-xl font-semibold text-accent-900 mb-4">{activeTab.replace("-", " ").toUpperCase()}</h2>
            <div className="bg-accent-50 p-4 rounded-lg">
              <p className="text-sm text-accent-700">
                {activeTab.replace("-", " ")} functionality will be implemented here.
              </p>
            </div>
          </div>
        )
    }
  }

  return (
    <Layout title="HR Manager Dashboard" onMenuToggle={() => setMobileSidebarOpen(!mobileSidebarOpen)}>
      {error && (
        <div className="fixed top-4 right-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded z-50 max-w-md">
          <div className="flex items-center">
            <AlertTriangle className="w-4 h-4 mr-2 flex-shrink-0" />
            <span className="text-sm">{error}</span>
            <button onClick={() => setError(null)} className="ml-2 flex-shrink-0">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
      {success && (
        <div className="fixed top-4 right-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded z-50 max-w-md">
          <div className="flex items-center">
            <CheckCircle className="w-4 h-4 mr-2 flex-shrink-0" />
            <span className="text-sm">{success}</span>
            <button onClick={() => setSuccess(null)} className="ml-2 flex-shrink-0">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Overlay for mobile sidebar */}
      {mobileSidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-0 lg:hidden" onClick={() => setMobileSidebarOpen(false)} />
      )}

      <div className="flex flex-1">
        {/* Sidebar Navigation - Fixed and Responsive */}
        <div
          className={`fixed top-0 sm:top-16 left-0 w-72 h-screen sm:h-[calc(100vh-4rem)] overflow-y-auto bg-gradient-to-b from-slate-50 to-slate-100 border-r border-slate-300 p-6 z-10
          ${mobileSidebarOpen ? "block" : "hidden"} lg:block`}
        >
          {/* Mobile header space */}
          <div className="h-14 sm:hidden"></div>
          
          {/* Campaign Status */}
          {isCampaignSelected && (
            <div className="mb-6 p-4 bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-300 rounded-xl shadow-sm">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
                <span className="text-xs font-semibold text-emerald-800 uppercase tracking-wide">Active Campaign</span>
              </div>
              <p className="text-sm text-emerald-700 font-semibold mb-3 truncate">
                {selectedCampaign?.name}
              </p>
              <button
                onClick={() => {
                  setSelectedCampaign(null)
                  handleTabChange('campaign-selector')
                }}
                className="text-xs text-emerald-600 hover:text-emerald-800 font-medium transition-colors"
              >
                Clear Selection
              </button>
            </div>
          )}
          
          <nav className="space-y-2">
            {navigationItems.map((item) => {
              const Icon = item.icon
              const isDisabled = item.id !== 'campaign-selector' && !isCampaignSelected
              const isActive = activeTab === item.id
              
              return (
                <button
                  key={item.id}
                  onClick={() => {
                    if (isDisabled) {
                      handleTabChange('campaign-selector')
                    } else {
                      handleTabChange(item.id)
                    }
                    setMobileSidebarOpen(false)
                  }}
                  disabled={isDisabled}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left rounded-xl transition-all duration-200 ${
                    isActive 
                      ? "bg-blue-600 text-white shadow-md" 
                      : isDisabled
                      ? "bg-slate-50 text-slate-400 cursor-not-allowed opacity-60"
                      : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                  }`}
                >
                  <Icon className={`w-5 h-5 flex-shrink-0 ${
                    isActive ? "text-white" : isDisabled ? "text-slate-400" : "text-slate-500"
                  }`} />
                  <span className="font-medium text-sm whitespace-nowrap overflow-hidden text-ellipsis">
                    {item.label}
                  </span>
                </button>
              )
            })}
          </nav>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 lg:ml-72 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pt-14 sm:pt-16">
          {renderContent()}
        </div>
      </div>
    </Layout>
  )
}
